import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../authContext/AuthProvider';
import '../styles/Auth.css';

const ResetPasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get token from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const urlToken = queryParams.get('token');

  const [formData, setFormData] = useState({
    token: urlToken || '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { resetPassword } = useAuth();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.token || formData.token.trim() === '') {
      newErrors.token = 'Reset token is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await resetPassword(formData.token, formData.password);
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Password reset successful! Please login with your new password.');
      navigate('/login');
    } else {
      toast.error(result.error || 'Failed to reset password. Please check your token and try again.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
          <h1>Reset Password</h1>
          <p>Enter the token from your email and your new password</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="token">Reset Token</label>
            <input
              type="text"
              id="token"
              name="token"
              value={formData.token}
              onChange={handleChange}
              placeholder="Paste your reset token from email"
              className={errors.token ? 'is-invalid' : ''}
              disabled={!!urlToken}
              style={{ fontFamily: 'monospace', fontSize: '14px' }}
            />
            {errors.token && <div className="error-text">{errors.token}</div>}
            {urlToken && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Token automatically filled from email link
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter new password (min. 8 characters)"
              className={errors.password ? 'is-invalid' : ''}
            />
            {errors.password && <div className="error-text">{errors.password}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              className={errors.confirmPassword ? 'is-invalid' : ''}
            />
            {errors.confirmPassword && (
              <div className="error-text">{errors.confirmPassword}</div>
            )}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="link-button"
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#4F46E5', 
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px'
              }}
            >
              Need a new reset token?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordScreen; 